import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import DragHandleIcon from '@material-ui/icons/DragHandle';

import { SdmTypography } from 'components';
import CustomAccordion from '../styledComponents/CustomAccordion';
import BlockAccordionTitle from '../styledComponents/BlockAccordionTitle';
import translations from 'utils/translations';
import { handleDropAfterErrors, ParagraphEnum } from 'utils/drupalEntities';

import SharedControllers from './SharedControllers';
import RHFAutocomplete from '../styledComponents/RHFAutocomplete';
import DropZonePreviewImage from '../styledComponents/DropZonePreviewImage';
import { Controller } from 'react-hook-form';
import CustomTextFieldComponent from '../styledComponents/CustomTextFieldComponent';
import RHFApiAutocomplete from '../styledComponents/RHFApiAutocomplete';
import { backHomeLink } from 'utils/constants';
import { useNetwork } from 'services/network';
import BoxContainer from '../styledComponents/BoxContainer';
import LabelContainer from './LabelContainer';
import NotTranslatableMsg from './NotTranslatableMsg';

const TechnicalSheetBlock = ({
  control,
  field,
  idx,
  additionalData,
  handleDelete,
  watch,
  setValue,
  setCustomErrors,
  getValues,
  langCode,
  productLangCode,
  setInfoDialog,
  ...listeners
}) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const network = useNetwork();

  // States
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [form, setForm] = useState();
  const [yearsOptions, setYearsOptions] = useState();
  const [materialOptions, setMaterialOptions] = useState([]);

  // Constants
  const fieldNames = [
    'field_anno',
    'field_caratteristiche',
    'field_collezione',
    'field_descrizione',
    'field_designer',
    'field_dimensioni',
    'field_materiali',
    'field_modello_3d',
    'field_processo_produttivo',
    'field_sheet_image',
    'field_website',
  ];

  const isEn = productLangCode === 'en';
  const isNotTranslatable = (key) => isEn && !form?.[key]?.isTranslatable;

  const isNotImgTranslatable = (key) => isEn && !form?.[key]?.isImgTranslatable;
  const isNotAltTranslatable = (key) => isEn && !form?.[key]?.isAltTranslatable;

  // Effects
  useEffect(() => {
    if (additionalData) {
      const fetchAutoCompleteOptions = async ({ entity, setState }) => {
        network
          .get(
            `${backHomeLink}/${langCode}/jsonapi/${entity.formEntityType}/${entity.formEntityBundle}`
          )
          .then((res) => {
            const data = res?.data?.data?.length
              ? res.data.data
                  .map((o) => ({
                    value: o.id,
                    label: o.attributes.name,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))
              : [];
            setState(data);
          });
      };
      Object.entries(additionalData).map(([k, v]) => {
        if (k === ParagraphEnum.TECHNICAL_SHEET) {
          const _form = {};
          fieldNames.map((n) => {
            const found = v.find((c) => c.name === n);
            if (found) _form[n] = found;
            if (n === 'field_anno') {
              // Creo le options per la select Anno
              const startYear = new Date().getFullYear();
              const stopYear = parseInt(found?.yearonly_from || '1970');
              const years = Array.from(
                {
                  length: (stopYear - startYear) / -1 + 1,
                },
                (_, i) => startYear + i * -1
              ).map((y) => ({
                value: `${y}` || '',
                label: `${y}` || '',
              }));
              setYearsOptions(years);
            }
            if (n === 'field_materiali') {
              // Fetcho le options per Autocomplete Materiali
              fetchAutoCompleteOptions({
                entity: found,
                setState: setMaterialOptions,
              });
            }
          });
          setForm(_form);
        }
      });
    }
  }, [additionalData]);

  // Functions
  const handleChange = (acceptedFiles) => {
    const c = form.field_sheet_image;
    handleDropAfterErrors({
      files: acceptedFiles,
      fieldName: `additionalContents[${idx}][${form.field_sheet_image.name}]`,
      maxResolution: c.maxResolution,
      maxSize: c.maxSize,
      fileExtension: c.extensions.split(' '),
      setCustomErrors,
      setValue,
      i18nErrors: i18n.errors,
    });
  };

  // Components

  return (
    <>
      {form && (
        <CustomAccordion
          onChange={(expanded) => {
            setIsAccordionOpen(expanded);
          }}
          expandIconVariant="secondary"
          title={
            <BlockAccordionTitle
              productLangCode={productLangCode}
              setInfoDialog={setInfoDialog}
              handleDelete={handleDelete}
              formKey={ParagraphEnum.TECHNICAL_SHEET}
              formTitle={i18n.titles.technicalSheet}
              langCode={langCode}
            >
              <div
                style={{ width: '100%', display: 'flex', alignItems: 'center' }}
              >
                {!isAccordionOpen && productLangCode?.toLowerCase() !== 'en' ? (
                  <div {...listeners} style={{ marginRight: '5px' }}>
                    <DragHandleIcon
                      style={{
                        height: '15px',
                        color: 'gray',
                      }}
                    />
                  </div>
                ) : null}
                <div>
                  <SdmTypography
                    variant={'h3'}
                    margin={0}
                    style={{ textTransform: 'uppercase' }}
                  >
                    {i18n.titles.technicalSheet}
                  </SdmTypography>
                </div>
              </div>
            </BlockAccordionTitle>
          }
        >
          {form.field_sheet_image && (
            // Dropzone per immagine
            <>
              <LabelContainer
                label={`${form.field_sheet_image?.label}`}
                isRequired={form.field_sheet_image?.isRequired}
                description={form.field_sheet_image?.description}
                hideSpacer
              />
              {isNotTranslatable('field_sheet_image') ? (
                <NotTranslatableMsg />
              ) : null}
              <DropZonePreviewImage
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                defaultValue={field[form.field_sheet_image.name]}
                altDefaultValue={field[`alt_${form.field_sheet_image.name}_0`]}
                name={`additionalContents[${idx}][${form.field_sheet_image.name}]`}
                altName={`additionalContents[${idx}][alt_${form.field_sheet_image.name}_0]`}
                accept={{
                  'image/*': form.field_sheet_image.extensions
                    ? form.field_sheet_image.extensions
                        .split(' ')
                        .map((e) => `.${e}`)
                    : ['jpg'],
                }}
                handleChange={handleChange}
                isMultiple={false}
                specs={[
                  i18n.imageSpecs.number,
                  `${i18n.imageSpecs.size} ${form.field_sheet_image.maxSize}`,
                  `${i18n.imageSpecs.format} ${form.field_sheet_image.extensions}`,
                  `${i18n.imageSpecs.logoSpecSize} ${form.field_sheet_image.maxResolution}px`,
                ]}
                maxResolution={form.field_sheet_image.maxResolution}
                disableImg={isNotImgTranslatable('field_sheet_image')}
                disableAlt={isNotAltTranslatable('field_sheet_image')}
                setCustomErrors={setCustomErrors}
              />
            </>
          )}
          {form.field_descrizione && (
            // TextArea per descrizione
            <>
              <LabelContainer
                label={`${form.field_descrizione?.label}`}
                description={form.field_descrizione?.description}
                isRequired={form.field_descrizione?.isRequired}
              />
              {isNotTranslatable('field_descrizione') ? (
                <NotTranslatableMsg />
              ) : null}
              <Controller
                name={`additionalContents[${idx}][${form.field_descrizione.name}]`}
                control={control}
                defaultValue={field[form.field_descrizione.name]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={form.field_descrizione.label}
                      variant="outlined"
                      onChange={(e) =>
                        !isNotTranslatable('field_descrizione') &&
                        props.onChange(e.target.value)
                      }
                      value={props.value}
                      multiline
                      rows={form.field_descrizione.rows || 5}
                      disabled={isNotTranslatable('field_descrizione')}
                    />
                  );
                }}
              />
            </>
          )}
          {form.field_anno && (
            // Autocomplete per Anno
            <>
              <LabelContainer
                label={`${form.field_anno.label}`}
                description={form.field_anno.description}
                isRequired={form.field_anno?.isRequired}
              />
              {isNotTranslatable('field_anno') ? <NotTranslatableMsg /> : null}
              <RHFAutocomplete
                options={yearsOptions}
                label={form.field_anno.label}
                name={`additionalContents[${idx}][${form.field_anno.name}]`}
                control={control}
                defaultValue={field[form.field_anno.name]}
                disabled={isNotTranslatable('field_anno')}
              />
            </>
          )}
          {form.field_designer && (
            // Autocomplete per Designer
            <>
              <LabelContainer
                label={`${form.field_designer.label}`}
                description={form.field_designer.description}
                isRequired={form.field_designer?.isRequired}
              />
              {isNotTranslatable('field_designer') ? (
                <NotTranslatableMsg />
              ) : null}
              <RHFApiAutocomplete
                control={control}
                name={`additionalContents[${idx}][${form.field_designer.name}]`}
                placeholder={
                  form.field_designer.placeholder || form.field_designer.label
                }
                label={form.field_designer.label}
                endpoint={`${backHomeLink}/${langCode}/jsonapi/${form.field_designer.formEntityType}/${form.field_designer.formEntityBundle}`}
                endpointEntity={'name'}
                freeSolo
                disabled={isNotTranslatable('field_designer')}
              />
            </>
          )}
          {form.field_materiali && materialOptions?.length && (
            // Autocomplete per Materiali
            <>
              <LabelContainer
                label={`${form.field_materiali.label}`}
                description={form.field_materiali.description}
                isRequired={form.field_materiali?.isRequired}
              />
              {isNotTranslatable('field_materiali') ? (
                <NotTranslatableMsg />
              ) : null}
              <RHFAutocomplete
                control={control}
                defaultValue={field[form.field_materiali.name]}
                options={materialOptions}
                label={form.field_materiali.label}
                name={`additionalContents[${idx}][${form.field_materiali.name}]`}
                multiple
                disabled={isNotTranslatable('field_materiali')}
              />
            </>
          )}
          {form.field_caratteristiche && (
            // Autocomplete per Caratteristiche
            <>
              <LabelContainer
                label={`${form.field_caratteristiche.label}`}
                description={form.field_caratteristiche.description}
                isRequired={form.field_caratteristiche?.isRequired}
              />
              {isNotTranslatable('field_caratteristiche') ? (
                <NotTranslatableMsg />
              ) : null}
              <RHFApiAutocomplete
                control={control}
                name={`additionalContents[${idx}][${form.field_caratteristiche.name}]`}
                placeholder={
                  form.field_caratteristiche.placeholder ||
                  form.field_caratteristiche.label
                }
                label={form.field_caratteristiche.label}
                endpoint={`${backHomeLink}/${langCode}/jsonapi/${form.field_caratteristiche.formEntityType}/${form.field_caratteristiche.formEntityBundle}`}
                endpointEntity={'name'}
                disabled={isNotTranslatable('field_caratteristiche')}
                charactersCount={1}
              />
            </>
          )}
          {form.field_processo_produttivo && (
            // TextArea per Processo Produttivo
            <>
              <LabelContainer
                label={`${form.field_processo_produttivo.label}`}
                description={form.field_processo_produttivo.description}
                isRequired={form.field_processo_produttivo?.isRequired}
              />
              {isNotTranslatable('field_processo_produttivo') ? (
                <NotTranslatableMsg />
              ) : null}
              <Controller
                name={`additionalContents[${idx}][${form.field_processo_produttivo.name}]`}
                control={control}
                defaultValue={field[form.field_processo_produttivo.name]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={form.field_processo_produttivo.label}
                      variant="outlined"
                      onChange={(e) =>
                        !isNotTranslatable('field_processo_produttivo') &&
                        props.onChange(e.target.value)
                      }
                      value={props.value}
                      multiline
                      rows={form.field_processo_produttivo.rows || 5}
                      disabled={isNotTranslatable('field_processo_produttivo')}
                    />
                  );
                }}
              />
            </>
          )}
          {form.field_dimensioni && (
            // TextArea per Dimensioni
            <>
              <LabelContainer
                label={`${form.field_dimensioni.label}`}
                description={form.field_dimensioni.description}
                isRequired={form.field_dimensioni?.isRequired}
              />
              {isNotTranslatable('field_dimensioni') ? (
                <NotTranslatableMsg />
              ) : null}
              <Controller
                name={`additionalContents[${idx}][${form.field_dimensioni.name}]`}
                control={control}
                defaultValue={field[form.field_dimensioni.name]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={form.field_dimensioni.label}
                      variant="outlined"
                      onChange={(e) =>
                        !isNotTranslatable('field_dimensioni') &&
                        props.onChange(e.target.value)
                      }
                      value={props.value}
                      multiline
                      rows={form.field_dimensioni.rows || 5}
                      disabled={isNotTranslatable('field_dimensioni')}
                    />
                  );
                }}
              />
            </>
          )}
          {form.field_website && (
            // TextField Per Sito Ufficiale
            <BoxContainer>
              <LabelContainer
                label={`${form.field_website.label}`}
                description={form.field_website.description}
                isRequired={form.field_website?.isRequired}
              />
              {isNotTranslatable('field_website') ? (
                <NotTranslatableMsg />
              ) : null}
              <Controller
                name={`additionalContents[${idx}][${form.field_website.name}]`}
                control={control}
                defaultValue={field[form.field_website.name]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={`${i18n.titles.url} ${form.field_website.label}`}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isNotTranslatable('field_website')}
                    />
                  );
                }}
              />
              <Controller
                name={`additionalContents[${idx}][alt_${form.field_website.name}_0]`}
                control={control}
                defaultValue={field[`alt_${form.field_website.name}_0`]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={`${i18n.titles.urlText} ${form.field_website.label}`}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isNotTranslatable('field_website')}
                    />
                  );
                }}
              />
            </BoxContainer>
          )}
          {form.field_modello_3d && (
            // TextArea per Scheda Prodotto
            <BoxContainer>
              <LabelContainer
                label={`${form.field_modello_3d.label}`}
                description={form.field_modello_3d.description}
                isRequired={form.field_modello_3d?.isRequired}
              />
              {isNotTranslatable('field_modello_3d') ? (
                <NotTranslatableMsg />
              ) : null}
              <Controller
                name={`additionalContents[${idx}][${form.field_modello_3d.name}]`}
                control={control}
                defaultValue={field[form.field_modello_3d.name]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={`${i18n.titles.url} ${form.field_modello_3d.label}`}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isNotTranslatable('field_modello_3d')}
                    />
                  );
                }}
              />
              <Controller
                name={`additionalContents[${idx}][alt_${form.field_modello_3d.name}_0]`}
                control={control}
                defaultValue={field[`alt_${form.field_modello_3d.name}_0`]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={`${i18n.titles.urlText} ${form.field_modello_3d.label}`}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isNotTranslatable('field_modello_3d')}
                    />
                  );
                }}
              />
            </BoxContainer>
          )}
          {form.field_collezione && (
            // TextArea per Collezione
            <BoxContainer>
              <LabelContainer
                label={`${form.field_collezione.label}`}
                description={form.field_collezione.description}
                isRequired={form.field_collezione?.isRequired}
              />
              {isNotTranslatable('field_collezione') ? (
                <NotTranslatableMsg />
              ) : null}
              <Controller
                name={`additionalContents[${idx}][${form.field_collezione.name}]`}
                control={control}
                defaultValue={field[form.field_collezione.name]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={`${i18n.titles.url} ${form.field_collezione.label}`}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isNotTranslatable('field_collezione')}
                    />
                  );
                }}
              />
              <Controller
                name={`additionalContents[${idx}][alt_${form.field_collezione.name}_0]`}
                control={control}
                defaultValue={field[`alt_${form.field_collezione.name}_0`]}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={`${i18n.titles.urlText} ${form.field_collezione.label}`}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isNotTranslatable('field_collezione')}
                    />
                  );
                }}
              />
            </BoxContainer>
          )}
          <SharedControllers control={control} field={field} idx={idx} />
        </CustomAccordion>
      )}
    </>
  );
};

export default TechnicalSheetBlock;
